/**
 * Fixed free question set.
 *
 * The free tier is a curated, fixed 24-question teaser (12 individual + 12 joint,
 * 2 per core topic) that spans comfort levels 1-3 to excite couples to continue.
 * It is shown in full regardless of the couple's chosen comfort level — see
 * IndividualWork / JointCollaboration, which exempt these IDs from comfort filtering.
 *
 * Everything else lives behind a purchase (premium packs) or is archived in the
 * admin-only `questionArchive` collection.
 *
 * Keep this list in sync with the archive migration (scripts/archive-questions.ts)
 * and the tag migration (scripts/tag-visible-questions.ts).
 */
export const FREE_QUESTION_IDS: ReadonlySet<string> = new Set([
  // Individual (12)
  'emotion-ind-L1-2', // "How do you typically let your partner know you're in a good mood?"
  'emotion-ind-L2-1', // "What emotions do you find most difficult to express to your partner?"
  'conflict-ind-L1-2', // "When you realize you've made a mistake that affected your partner..."
  'conflict-ind-L2-1', // "What topics tend to escalate quickly from minor discussion to heated..."
  'lifestyle.1', // "What does your ideal weekend look like?"
  'love-ind-L3-2', // "What makes you feel most desired by your partner, beyond just physical..."
  'family-ind-L1-1', // "What positive traditions or habits from your family would you like to carry forward?"
  'family-ind-L1-5', // "What meals or foods hold special meaning from your upbringing?"
  'vision-ind-L1-2', // "How important is it for you to live near nature, urban amenities, or cultural activities?"
  'career.3', // "What career changes are you considering but haven't fully shared?"
  'meta-ind-L1-1', // "How do you prefer to receive feedback about things in the relationship?"
  'meta-ind-L3-4', // "How have you grown or changed because of this relationship, positively?"

  // Joint (12)
  'j-emotion-L1-2', // "What daily greeting or parting rituals feel meaningful to us?"
  'j-emotion-L2-1', // "How will we handle situations where one of us needs connection and the other doesn't?"
  'j-conflict-L1-2', // "How will we decide when an issue is important enough to discuss vs. let go?"
  'j-conflict-L2-2', // "How will we revisit recurring disagreements that never seem fully resolved?"
  'j-love-L2-4', // "What does an ideal date night look like for us, and how often?"
  'j-adventure.1', // "How will we keep our relationship fresh and exciting over time?"
  'j-family-L1-3', // "What new traditions do we want to create that are uniquely ours?"
  'j-family-L3-3', // "What aspects of our upbringings do we explicitly not want to repeat?"
  'j-vision-L1-1', // "What kind of social life do we want to have as a couple?"
  'j-community.2', // "What shared service or volunteer work appeals to us?"
  'j-maintain.4', // "What are our non-negotiable weekly/monthly couple activities?"
  'j-meta-L2-1', // "What books, podcasts, or resources about relationships would we explore?"
]);

/** True when the question id belongs to the fixed free set. */
export function isFreeQuestion(id: string): boolean {
  return FREE_QUESTION_IDS.has(id);
}
